import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Check, Loader2 } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("polling"); // polling | success | failed | expired
  const [plan, setPlan] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate("/dashboard");
      return;
    }
    let cancelled = false;
    let n = 0;
    const poll = async () => {
      if (cancelled) return;
      n += 1;
      setAttempts(n);
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setPlan(res.data.plan_id);
          setStatus("success");
          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (n >= 8) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (n >= 8) setStatus("failed");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain flex items-center justify-center px-6">
      <div className="max-w-xl w-full text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-[#D95B38] mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-serif text-4xl mb-3">Confirming your payment…</h1>
            <p className="font-mono text-sm text-[#A39A8F]">Attempt {attempts} / 8 · usually under 5 seconds</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-[#D95B38] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#F5F2EB]" strokeWidth={2} />
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#D95B38] mb-3">Payment successful</div>
            <h1 className="font-serif text-5xl mb-3" data-testid="billing-success-title">Welcome to Pro.</h1>
            <p className="font-mono text-sm text-[#A39A8F] mb-10">
              Your <span className="text-[#F5F2EB]">{plan}</span> plan is now active. Build agents to your heart's content.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="billing-go-dashboard"
              className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-8 py-4 btn-press"
            >
              Go to dashboard →
            </button>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <h1 className="font-serif text-4xl mb-3">Hmm, that didn't go through.</h1>
            <p className="font-mono text-sm text-[#A39A8F] mb-10">
              {status === "expired" ? "The checkout session expired." : "We couldn't confirm payment in time."}
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] px-8 py-4 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors"
            >
              Back to dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
